import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { signInWithAuth0Token } from '../services/firebase';

export const useAuth0Firebase = () => {
  const {
    getAccessTokenSilently,
    isAuthenticated,
    user: auth0User,
    isLoading: isAuth0Loading,
    logout,
    loginWithRedirect,
    handleRedirectCallback,
  } = useAuth0();
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [isFirebaseLoading, setIsFirebaseLoading] = useState(true);
  const [error, setError] = useState(null);
  const isAuthenticating = useRef(false);
  const authAttempts = useRef(0);
  const mounted = useRef(true);
  const lastAuth0User = useRef(null);
  const [initialLoadFlag, setInitialLoadFlag] = useState(true);
  const [isHandlingRedirect, setIsHandlingRedirect] = useState(false);
  const [user, setUser] = useState(null);

  // Update user state when auth0User changes
  useEffect(() => {
    if (auth0User) {
      setUser(auth0User);
    }
  }, [auth0User]);

  // Log when Auth0 SDK loading state changes
  useEffect(() => {
    if (!isAuth0Loading && initialLoadFlag) {
      console.log(
        '[useAuth0Firebase] Auth0 SDK finished loading AND initialLoadFlag is true.',
      );
    }
  }, [isAuth0Loading, initialLoadFlag]);

  // Handle Auth0 redirect callback
  useEffect(() => {
    const processAuth0Redirect = async () => {
      if (
        window.location.search.includes('code=') &&
        window.location.search.includes('state=')
      ) {
        setIsHandlingRedirect(true);
        try {
          const currentUrl = window.location.href;
          await handleRedirectCallback();

          if (window.location.href === currentUrl) {
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname,
            );
          }
        } catch (e) {
          console.error(
            '[useAuth0Firebase] Error processing Auth0 redirect callback:',
            e,
          );
          setError(e);
          if (mounted.current && !e.message?.includes('Invalid state')) {
            loginWithRedirect();
          }
        } finally {
          if (mounted.current) setIsHandlingRedirect(false);
        }
      }
    };
    processAuth0Redirect();
  }, [handleRedirectCallback, loginWithRedirect]);

  // Cleanup on unmount
  useEffect(() => {
    console.log('[useAuth0Firebase] Mount/unmount effect setup.');
    mounted.current = true;
    return () => {
      console.log('[useAuth0Firebase] Component unmounting (cleanup)...');
      mounted.current = false;
      isAuthenticating.current = false;
      authAttempts.current = 0;
    };
  }, []);

  const handleAuthError = useCallback(
    async (authErr) => {
      console.error('[useAuth0Firebase] Auth error:', authErr);
      if (!mounted.current) return;

      setFirebaseUser(null);
      setError(authErr);
      setIsFirebaseLoading(false);
      setInitialLoadFlag(false);

      if (
        authErr.message.includes('Failed to get Firebase token') ||
        authErr.message.includes('Too many authentication attempts')
      ) {
        console.log('[useAuth0Firebase] Critical error, logging out...');
        try {
          await logout({ logoutParams: { returnTo: window.location.origin } });
        } catch (logoutError) {
          console.error('[useAuth0Firebase] Logout error:', logoutError);
        }
      }
    },
    [logout],
  );

  useEffect(() => {
    let fbUserInTryScope = null;

    const syncAuth0WithFirebase = async () => {
      if (!mounted.current || isHandlingRedirect) {
        return;
      }

      if (isAuth0Loading) {
        return;
      }

      if (!isAuthenticated || !auth0User) {
        if (mounted.current) {
          setFirebaseUser(null);
          setIsFirebaseLoading(false);
          setError(null);
          isAuthenticating.current = false;
          authAttempts.current = 0;
          lastAuth0User.current = null;
          setInitialLoadFlag(false);

          if (
            !isAuthenticated &&
            window.location.pathname !== '/access-denied'
          ) {
            loginWithRedirect();
          }
        }
        return;
      }

      // Prevent duplicate authentication attempts
      if (isAuthenticating.current) {
        console.log('[useAuth0Firebase] Already authenticating, skipping...');
        return;
      }

      // Check if we need to re-authenticate
      if (
        lastAuth0User.current?.sub === auth0User.sub &&
        firebaseUser &&
        !isFirebaseLoading
      ) {
        console.log(
          '[useAuth0Firebase] User already authenticated with Firebase',
        );
        return;
      }

      try {
        isAuthenticating.current = true;
        const token = await getAccessTokenSilently();
        fbUserInTryScope = await signInWithAuth0Token(token);

        if (mounted.current) {
          setFirebaseUser(fbUserInTryScope);
          setIsFirebaseLoading(false);
          setError(null);
          lastAuth0User.current = auth0User;
          setInitialLoadFlag(false);
        }
      } catch (err) {
        if (mounted.current) {
          handleAuthError(err);
        }
      } finally {
        if (mounted.current) {
          isAuthenticating.current = false;
        }
      }
    };

    syncAuth0WithFirebase();
  }, [
    isAuthenticated,
    auth0User,
    isAuth0Loading,
    isHandlingRedirect,
    getAccessTokenSilently,
    handleAuthError,
    firebaseUser,
    isFirebaseLoading,
  ]);

  return {
    user,
    isAuthenticated: isAuthenticated && !!firebaseUser,
    isFirebaseAuthenticated: !!firebaseUser,
    isLoading: isAuth0Loading || isFirebaseLoading,
    error,
    logout,
  };
};
